import { TIME_CODE_MODE } from 'config/constants/timecode'
import { TimeCodeModeType } from 'types/timecode'
import { secondsToTimelineTime, timeToFractions } from 'Util'

export type TIMECODE = number | string | Date | Timecode;
export type FRAMERATE = 23.976 | 24 | 25 | 29.97 | 30 | 50 | 59.94 | 60 | [number, number];

type TimeCodeReturnType = { hours: number, minutes: number, seconds: number, frames: number }

export const frameNumberToSeconds = (frames: number, fps: number) => frames / fps

/*
 * These calculations are necessary because of the loss of precision
 * when multiplying floating point numbers in JavaScript.
 */
export const secondsToFrameNumber = (sec: number, fps: number) => Math.round(sec * fps)

export const isDropFrameTimeCode = (mode: TimeCodeModeType, fps: number) => (
  (mode === TIME_CODE_MODE.FRAME_DF) && ([ 29.97, 59.94 ].includes(Number(fps.toFixed(2))))
)

export const getObjectTimeCode = (progress: number, fps: number, timeMode: TimeCodeModeType) => {
  const isDF = isDropFrameTimeCode(timeMode, fps)
  const isFrameMode = timeMode === TIME_CODE_MODE.FRAME_DF || timeMode === TIME_CODE_MODE.FRAME_NDF
  const frameCount = secondsToFrameNumber(progress, fps)
  let timeCodeData = {} as TimeCodeReturnType
  let timeFractions = {} as Omit<TimeCodeReturnType, 'frames'> & { milliseconds: number }
  try {
    const { hours, minutes, seconds, frames } = new Timecode(frameCount, fps as FRAMERATE, isDF)
    timeFractions = timeToFractions(secondsToTimelineTime(progress))
    timeCodeData = { hours, minutes, seconds, frames }
  } catch {
    timeFractions = timeToFractions(secondsToTimelineTime(progress))
    timeCodeData = { ...timeFractions, frames: secondsToFrameNumber(timeFractions.milliseconds / 1000, fps) }
  }

  const { hours, minutes, seconds, frames } = timeCodeData

  return {
    hours: isFrameMode ? hours : timeFractions.hours,
    minutes: isFrameMode ? minutes : timeFractions.minutes,
    seconds: isFrameMode ? seconds : timeFractions.seconds,
    frames,
    milliseconds: timeFractions.milliseconds,
    frameCount,
    dropFrame: isDF,
  }
}

// smpte-timecode extended fork - https://github.com/CrystalComputerCorp/smpte-timecode/blob/master/smpte-timecode.js
export default class Timecode {

  private _dropFrame: boolean
  private _frameRateDen = 0
  private _frameRateNum = 0
  private _frameRate = 23.976
  private _frameCount = 0
  private _frames = 0
  private _hours = 0
  private _minutes = 0
  private _seconds = 0

  // eslint-disable-next-line
  constructor(timeCode: TIMECODE, frameRate: FRAMERATE, dropFrame?: boolean) {
    this._dropFrame = Boolean(dropFrame)

    switch (typeof (frameRate)) {
      case 'undefined':
        this._frameRateNum = 30000
        this._frameRateDen = 1001
        this._dropFrame = true
        break
      case 'object':
        if (Array.isArray(frameRate)
          && frameRate.length >= 2
          && typeof frameRate[0] == 'number'
          && typeof frameRate[1] == 'number') {
          // eslint-disable-next-line prefer-destructuring
          this._frameRateNum = frameRate[0]
          // eslint-disable-next-line prefer-destructuring
          this._frameRateDen = frameRate[1]
        }
        break
      case 'number':
        if (frameRate > 0) {
          const frameRateRound = Math.round(frameRate)
          if (frameRate === frameRateRound) {
            this._frameRateNum = frameRate
            this._frameRateDen = 1
          } else if (frameRate < frameRateRound
            && (frameRateRound === 24 || frameRateRound === 30 || frameRateRound === 60)) {
            this._frameRateNum = frameRateRound * 1000
            this._frameRateDen = 1001
            if (frameRateRound !== 24 && typeof dropFrame !== 'boolean') this._dropFrame = true
          }
        }
        break
    }

    if (this._frameRateDen === 0) {
      throw new Error('Invalid framerate. Either a number or an array of [numerator,denominator] are expected.')
    }

    this._frameRate = this._frameRateNum / this._frameRateDen

    if (this._frameRateDen !== 1) this._frameRate = Math.round((this._frameRate + Number.EPSILON) * 100) / 100

    if (typeof timeCode === 'number') {
      this._frameCount = Math.round(timeCode)
      this._frameCountToTimeCode()
    } else if (typeof timeCode === 'string') {
      // const parts = timeCode.match('^([012]\\d):(\\d\\d):(\\d\\d)(:|;|\\.)(\\d+)$')
      const parts = timeCode.match('^(\\d+):(\\d\\d):(\\d\\d)(:|;|\\.)(\\d+)$') // (patched for more 29 hours)

      if (!parts) throw new Error('Timecode string expected as HH:MM:SS:FF or HH:MM:SS;FF')

      this._hours = parseInt(parts[1])
      this._minutes = parseInt(parts[2])
      this._seconds = parseInt(parts[3])

      if (typeof dropFrame !== 'boolean') {
        this._dropFrame = (parts[4] !== ':' && this._frameRate > 25)
      }
      this._frames = parseInt(parts[5])
      this._timeCodeToFrameCount()
    } else if (timeCode instanceof Date) {
      const midnight = new Date(timeCode.getFullYear(), timeCode.getMonth(), timeCode.getDate(), 0, 0, 0)
      const midnightTz = midnight.getTimezoneOffset() * 60 * 1000
      const timecodeTz = timeCode.getTimezoneOffset() * 60 * 1000
      this._frameCount = Math.round(
        ((timeCode.getTime() - midnight.getTime() + (midnightTz - timecodeTz)) * this._frameRate) / 1000
      )
      this._frameCountToTimeCode()
    } else if (timeCode instanceof Timecode && typeof timeCode.hours != 'undefined') {
      if (!frameRate && timeCode.frameRate) {
        this._frameRate = timeCode.frameRate
        this._frameRateDen = timeCode.frameRateDen
        this._frameRateNum = timeCode.frameRateNum
      }

      this._dropFrame = timeCode.dropFrame
      this._hours = timeCode.hours
      this._minutes = timeCode.minutes
      this._seconds = timeCode.seconds
      this._frames = timeCode.frames
      this._timeCodeToFrameCount()
    } else if (typeof timeCode === 'undefined') {
      this._frameCount = 0
    } else {
      throw new Error('Timecode() constructor expects a number, timecode string, or Date()')
    }

    this._validate(timeCode)
  }

  // eslint-disable-next-line
  public get dropFrame(): boolean {
    return this._dropFrame
  }
  // eslint-disable-next-line
  public get frameRateDen(): number {
    return this._frameRateDen
  }
  // eslint-disable-next-line
  public get frameRateNum(): number {
    return this._frameRateNum
  }
  // eslint-disable-next-line
  public get frameRate(): number {
    return this._frameRate
  }
  // eslint-disable-next-line
  public get frameCount(): number {
    return this._frameCount
  }
  // eslint-disable-next-line
  public get frames(): number {
    return this._frames
  }
  // eslint-disable-next-line
  public get hours(): number {
    return this._hours
  }
  // eslint-disable-next-line
  public get minutes(): number {
    return this._minutes
  }
  // eslint-disable-next-line
  public get seconds(): number {
    return this._seconds
  }

  /**
   * Validates timecode
   * @private
   * @param {number|String|Date|Object} timeCode for the reference
   */
  private _validate(timeCode: TIMECODE): void {
    // Make sure dropFrame is only for 29.97 & 59.94
    if (this._dropFrame && this._frameRateDen !== 1001) {
      throw new Error('Drop frame is only supported for 23.976, 29.97, and 59.94 fps')
    }

    // make sure the numbers make sense (patched for more 23:59:59:999)
    if (
      // this._hours > 23 ||
      this._minutes > 59 || this._seconds > 59 || this._frames >= this._frameRate
      || (this._dropFrame && this._seconds === 0 && this._minutes % 10 && this._frames < 2 * (this._frameRate / 29.97))
    ) {
      throw new Error(`Invalid timecode ${JSON.stringify(timeCode)}`)
    }
  }

  private _frameCountToTimeCode(): void {
    let fc = this._frameCount
    // adjust for dropFrame
    if (this._dropFrame) {
      const df = this._frameRate <= 30 ? 2 : 4 // 59.94 skips 4 frames
      const d = Math.floor(this._frameCount / (17982 * df / 2))
      let m = this._frameCount % (17982 * df / 2)

      if (m < df) m += df

      fc += 9 * df * d + df * Math.floor((m - df) / (1798 * df / 2))
    }
    const fps = Math.round(this._frameRate)
    this._frames = fc % fps
    this._seconds = Math.floor(fc / fps) % 60
    this._minutes = Math.floor(fc / (fps * 60)) % 60
    this._hours = Math.floor(fc / (fps * 3600))
  }

  private _timeCodeToFrameCount(): void {
    this._frameCount = (this._hours * 3600 + this._minutes * 60 + this._seconds)
      * Math.round(this._frameRate) + this._frames
    // adjust for dropFrame
    if (this._dropFrame) {
      const totalMinutes = this._hours * 60 + this._minutes
      const df = this._frameRate < 30 ? 2 : 4
      this._frameCount -= df * (totalMinutes - Math.floor(totalMinutes / 10))
    }
  }
  // eslint-disable-next-line
  public toString(format?: string): string {
    let frames = this._frames
    let field = ''

    if (typeof format === 'string') {
      if (format === 'field') {
        if (this._frameRate <= 30) {
          field = '.0'
        } else {
          frames = Math.floor(frames / 2)
          field = '.'.concat((this._frameCount % 2).toString())
        }
      } else throw new Error('Unsupported string format')
    }

    return ''.concat(
      String(this._hours).padStart(2, '0'),
      ':',
      String(this._minutes).padStart(2, '0'),
      ':',
      String(this._seconds).padStart(2, '0'),
      this._dropFrame ? ';' : ':',
      String(frames).padStart(String(Math.round(this._frameRate)).length, '0'),
      field
    )
  }
  // eslint-disable-next-line
  public valueOf(): number {
    return this._frameCount
  }
  // eslint-disable-next-line
  public toDate(): Date {
    const ms = this._frameCount / (this._frameRateNum / this._frameRateDen) * 1000
    const midnight = new Date()
    midnight.setHours(0)
    midnight.setMinutes(0)
    midnight.setSeconds(0)
    midnight.setMilliseconds(0)

    const d = new Date(midnight.valueOf() + ms)
    const midnightTz = midnight.getTimezoneOffset() * 60 * 1000
    const timecodeTz = d.getTimezoneOffset() * 60 * 1000
    return new Date(midnight.valueOf() + ms + (timecodeTz - midnightTz))
  }
  // eslint-disable-next-line
  public subtract(t: TIMECODE, rollOverMaxHours: number): this {
    return this.add(t, true, rollOverMaxHours)
  }
  // eslint-disable-next-line
  public add(t: TIMECODE, negative= false, rollOverMaxHours: number = 0): this {
    if (typeof t === 'number') {
      let newFrameCount = this._frameCount + Math.round(t) * (negative ? -1 : 1)
      if (newFrameCount < 0 && rollOverMaxHours > 0) {
        newFrameCount = (Math.round(this._frameRate * 86400)) + newFrameCount
        if (((newFrameCount / this._frameRate) / 3600) > rollOverMaxHours) {
          throw new Error('Rollover arithmetic exceeds max permitted')
        }
      }
      if (newFrameCount < 0) {
        throw new Error('Negative timecodes not supported')
      }
      this._frameCount = newFrameCount
    } else {
      const result = !(t instanceof Timecode)
        ? new Timecode(t, [ this._frameRateNum, this._frameRateDen ], this._dropFrame)
        : t

      return this.add(result.frameCount, negative, rollOverMaxHours)
    }

    // this._frameCount %= (Math.round(this._frameRate * 86400)) // wraparound 24h (patched for more 23:59:59:999)
    this._frameCountToTimeCode()
    return this
  }

}
